import { type IChatMember } from './chat-member._._definitions'
import { CHAT_TYPE, type IChat } from './chat._._definitions'
import { mapChatFields } from './chat._._utils'
import { mapMessagesFields } from './message._._utils'
import { type IUser, USER_TYPE } from './user._._definitions'
import { mapUserFields } from './user._._utils'

export const mapChatMembersFields = (
	chatMembers: IChatMember[],
): IChatMember[] => chatMembers.map(mapChatMemberFields)

export const mapChatMemberFields = (chatMember: IChatMember): IChatMember => ({
	...chatMember,
	...(chatMember.user && { user: mapUserFields(chatMember.user) as IUser }),
	...(chatMember.chat && { chat: mapChatFields(chatMember.chat) as IChat }),
	...(chatMember.messages && {
		messages: mapMessagesFields(chatMember.messages).filter(Boolean),
	}),
})

export const findChatMember = (
	chatMembers: IChatMember[],
	memberId: string,
) => {
	return chatMembers.find((member) => member.id === memberId)
}

export const findChatMemberUser = (chatMembers: IChatMember[]) => {
	return chatMembers.find(
		(member) => !member.memberIsAi || member.user?.type === USER_TYPE.USER,
	)
}

export const findChatMemberAi = (chatMembers: IChatMember[]) => {
	return chatMembers.find(
		(member) => member.memberIsAi || member.user?.type === USER_TYPE.AI,
	)
}
export const getChatMemberAiUser = (
	chatMembers: IChatMember[],
): IUser | undefined => {
	return findChatMemberAi(chatMembers)?.user
}

export const findChatMemberByUserId = (
	chatMembers: IChatMember[],
	userId: string,
): IChatMember | undefined => {
	return chatMembers.find(
		(member) => member.userId === userId || member.user?.id === userId,
	)
}

export const filterUserChatMembers = (chatMembers: IChatMember[]) => {
	return chatMembers.filter((member) => !member.memberIsAi)
}

export const getChatMemberUsers = (chatMembers: IChatMember[]): IUser[] => {
	return filterUserChatMembers(chatMembers)
		.map((member) => member.user)
		.filter((user) => user !== undefined) as IUser[]
}

export const filterAiChatMembers = (chatMembers: IChatMember[]) => {
	return chatMembers.filter((member) => member.memberIsAi)
}

export const filterAvailableAiUsers = (
	availableAiUsers: IUser[],
	chatMembers: IChatMember[],
) => {
	return availableAiUsers.filter(
		(aiUser) => !chatMembers.some((member) => member.userId === aiUser.id),
	)
}

export const chatMemberHasUnreadMessages = (chatMember: IChatMember) => {
	const lastMessageIndex = chatMember.chat?.messages?.[0]?.index ?? -1
	const lastMessageIndexRead = chatMember.lastMessageIndexRead ?? -1
	return lastMessageIndex > lastMessageIndexRead
}

export const filterUnreadChatMembers = (chatMembers: IChatMember[]) =>
	chatMembers.filter(chatMemberHasUnreadMessages)

export const partitionUnreadChatMembersByChatType = (
	chatMembers: IChatMember[],
) => {
	const unreadChatMembers = filterUnreadChatMembers(chatMembers)

	return {
		aiChatMembers: unreadChatMembers.filter(
			(chatMember) => chatMember.chat?.type === CHAT_TYPE.AI,
		),
		groupChatMembers: unreadChatMembers.filter(
			(chatMember) => chatMember.chat?.type === CHAT_TYPE.GROUP,
		),
	}
}
